import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { NotificationType } from '../../types';

export function getHeaderHelper(locationPathName, Routes) {
  switch (locationPathName) {
    case Routes.SEQUENCE:
      return Routes.SEQUENCE[0].toUpperCase() + Routes.SEQUENCE.slice(1);
    case Routes.TASKS:
      return Routes.TASKS[0].toUpperCase() + Routes.TASKS.slice(1);
    case Routes.AGENCY_CLIENT:
      return 'Client Management';
    case Routes.UNIFIED_INBOX:
      return 'Unified Inbox';
    case Routes.CRM:
      return 'CRM';
    // return Routes.CRM[0].toUpperCase() + Routes.CRM.slice(1);
    case Routes.EMAIL_ACCOUNTS:
      return 'Email Accounts';
    case Routes.DOMAINS:
      return 'Domains';
    case Routes.INFRAMAIL_IPS:
      return 'Inframail IPs';
    case Routes.TEMPLATES:
      return Routes.TEMPLATES[0].toUpperCase() + Routes.TEMPLATES.slice(1);
    case Routes.REPORTS:
      return 'Analytics';
    case Routes.EMAIL_INSIGHTS:
      return '1:1 Email Insights';
    case Routes.EMAIL_WARMUP:
      return (
        Routes.EMAIL_WARMUP[0].toUpperCase() + Routes.EMAIL_WARMUP.slice(1)
      );
    case Routes.GROWTH_HUB:
      return 'Growth Hub';
    case Routes.AGENCY_PORTAL:
      return 'Agency Portal';
    case Routes.SETTINGS:
      return Routes.SETTINGS[0].toUpperCase() + Routes.SETTINGS.slice(1);
    case Routes.LEADS:
      return 'Lead Finder';
    case Routes.EMAIL_VERIFIER:
      return 'Email Verifier';
    case Routes.INBOX_RADAR:
      return 'Inbox Radar';
    case Routes.BILLING:
      return hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
        ? 'Billing & Subscription'
        : 'Plan details & Billing';
    case Routes.DIALER:
      return 'Dialer';
    default:
      return '';
  }
}

export function routeStateChange({
  getHeader,
  path,
  Routes,
  sequenceRoutes,
  setSingleSequenceHeader,
  emailWarmupRoutes,
  setEmailWarmupHeader,
  setEmailWarmupHeaderWithReport,
  setCreateEmailInfrastructureHeader,
  setAddEmailAccountsHeader,
  setEmailAccountsHeader,
  setPurchasePhoneNumberHeader,
  setPhoneNumberSettingsHeader,
  setHeaderTitle,
}) {
  if (
    getHeader(path?.[1]).toLowerCase() === Routes.SEQUENCE &&
    sequenceRoutes.includes(path?.[3])
  ) {
    setSingleSequenceHeader(true);
  } else {
    setSingleSequenceHeader(false);
  }

  if (getHeader(path?.[1]).toLowerCase() === Routes.EMAIL_WARMUP) {
    if (emailWarmupRoutes.includes(path?.[3])) {
      setEmailWarmupHeader(true);
      setEmailWarmupHeaderWithReport(true);
    } else {
      setEmailWarmupHeader(true);
      setEmailWarmupHeaderWithReport(false);
    }
  } else {
    setEmailWarmupHeader(false);
    setEmailWarmupHeaderWithReport(false);
  }

  if (path?.[1] === Routes.EMAIL_ACCOUNTS) {
    if (path?.[2] === 'create-email-accounts') {
      setCreateEmailInfrastructureHeader(true);
      setEmailAccountsHeader(false);
      setAddEmailAccountsHeader(false);
    } else if (path?.[2] === 'add-email-accounts') {
      setAddEmailAccountsHeader(true);
      setEmailAccountsHeader(false);
      setCreateEmailInfrastructureHeader(false);
    } else {
      setEmailAccountsHeader(true);
      setCreateEmailInfrastructureHeader(false);
      setAddEmailAccountsHeader(false);
    }
  } else {
    setEmailAccountsHeader(false);
    setCreateEmailInfrastructureHeader(false);
    setAddEmailAccountsHeader(false);
  }
  // changing header as the page changes
  if (path?.[1] === 'v2') {
    setHeaderTitle(getHeader(path?.[2]));
  } else {
    setHeaderTitle(getHeader(path?.[1]));
  }
  if (path?.[1] === Routes.DIALER) {
    if (path?.[2] === 'phone-numbers') {
      if (path?.[3] === 'purchase') {
        setPurchasePhoneNumberHeader(true);
        setPhoneNumberSettingsHeader(false);
        setHeaderTitle('');
      } else if (path?.[4] === 'settings') {
        setPurchasePhoneNumberHeader(false);
        setPhoneNumberSettingsHeader(true);
        setHeaderTitle('');
      } else {
        setPurchasePhoneNumberHeader(false);
        setPhoneNumberSettingsHeader(false);
        setHeaderTitle('Phone Numbers');
      }
    } else {
      setPurchasePhoneNumberHeader(false);
      setPhoneNumberSettingsHeader(false);
      setHeaderTitle('Dialer');
    }
  } else if (path?.[1] === 'phone-numbers' && path?.[3] === 'settings') {
    setPurchasePhoneNumberHeader(false);
    setPhoneNumberSettingsHeader(true);
    setHeaderTitle('');
  } else {
    setPhoneNumberSettingsHeader(false);
  }
}

export const formatNumberToK = (num: number): string => {
  if (num >= 1000) {
    const numberString = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
    const numberStringArr = numberString?.split('.');
    if (numberStringArr?.length === 2 && numberStringArr?.[1] === '0') {
      numberStringArr.pop();
    }
    return `${numberStringArr?.join('.')}K`;
  }
  return num?.toString();
};

export const formatLargeNumberToK = (num: number): string => {
  if (num >= 10_000) {
    const formatted = (num / 1_000).toFixed(1);
    return formatted.endsWith('.0')
      ? `${parseInt(formatted, 10)}K`
      : `${formatted}K`;
  }

  return num?.toString();
};

export const getProgressBarText = (
  type: NotificationType,
  totalCount: number,
) => {
  if (
    type === NotificationType.ProspectImportAsync ||
    type === NotificationType.SequenceProspectImportAsync
  ) {
    return `Importing ${formatNumberToK(totalCount)} Prospects`;
  }
  if (
    type === NotificationType.LeadRevealCompletedAsync ||
    type === NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailAsync ||
    type === NotificationType.LeadRevealV2EmailPhoneAsync ||
    type === NotificationType.LeadRevealV2PhoneAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailAsync
  ) {
    return `Revealing ${formatNumberToK(totalCount)} Leads`;
  }
  if (
    type === NotificationType.CompanyEnrichV2Async ||
    type === NotificationType.CompanyEnrichFreeV2Async
  ) {
    return `Enriching ${formatNumberToK(totalCount)} Companies`;
  }
  if (type === NotificationType.CompanyRevealV2Async) {
    return `Revealing ${formatNumberToK(totalCount)} Companies`;
  }
  if (type === NotificationType.EmailVerifier) {
    return `Verifying ${formatNumberToK(totalCount)} Emails`;
  }
  if (type === NotificationType.AiColumns) {
    return `Generating ${formatLargeNumberToK(totalCount)} Cells`;
  }
  return '';
};

export const getProgressBarCompletionText = (
  type: NotificationType,
  totalCount: number,
  failedCount?: number,
) => {
  if (
    type === NotificationType.ProspectImportAsync ||
    type === NotificationType.SequenceProspectImportAsync
  ) {
    return `Imported ${formatNumberToK(totalCount)} Prospects`;
  }
  if (
    type === NotificationType.LeadRevealCompletedAsync ||
    type === NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailAsync ||
    type === NotificationType.LeadRevealV2EmailPhoneAsync ||
    type === NotificationType.LeadRevealV2PhoneAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailAsync
  ) {
    return `Revealed ${formatNumberToK(totalCount)} Leads`;
  }
  if (
    type === NotificationType.CompanyEnrichV2Async ||
    type === NotificationType.CompanyEnrichFreeV2Async
  ) {
    return `Enriched ${formatNumberToK(totalCount)} Companies`;
  }
  if (type === NotificationType.CompanyRevealV2Async) {
    return `Revealed ${formatNumberToK(totalCount)} Companies`;
  }
  if (type === NotificationType.EmailVerifier) {
    return `Verified ${formatNumberToK(totalCount)} Emails`;
  }
  if (type === NotificationType.AiColumns) {
    if (failedCount && failedCount > 0) {
      return `Generated ${formatLargeNumberToK(
        totalCount - failedCount,
      )} Cells.`;
    }
    return `Generated ${formatLargeNumberToK(totalCount)} Cells`;
  }
  if (type === NotificationType.AiColumnsFailed) {
    return `${formatLargeNumberToK(
      totalCount - failedCount,
    )}/${formatLargeNumberToK(totalCount)} Cells Generated.`;
  }
  return '';
};

export const getCtaText = (
  type: NotificationType,
  notification?: Notification,
  completedAt?: Notification,
) => {
  if (
    type === NotificationType.ProspectImportAsync ||
    type === NotificationType.SequenceProspectImportAsync
  ) {
    return 'View List';
  }

  if (
    notification &&
    (type === NotificationType.LeadRevealCompletedAsync ||
      type === NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
      type === NotificationType.LeadRevealV2EmailAsync ||
      type === NotificationType.LeadRevealV2EmailPhoneAsync ||
      type === NotificationType.LeadRevealV2PhoneAsync ||
      type === NotificationType.LeadRevealV2PersonalEmailAsync ||
      type === NotificationType.LeadRevealV2PersonalEmailPhoneAsync ||
      type === NotificationType.LeadRevealV2EmailPersonalEmailPhoneAsync ||
      type === NotificationType.LeadRevealV2EmailPersonalEmailAsync)
  ) {
    return 'View Report';
  }

  if (
    notification &&
    (type === NotificationType.CompanyEnrichV2Async ||
      type === NotificationType.CompanyRevealV2Async ||
      type === NotificationType.CompanyEnrichFreeV2Async)
  ) {
    return 'View Report';
  }

  if (
    type === NotificationType.LeadRevealCompletedAsync ||
    type === NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailAsync ||
    type === NotificationType.LeadRevealV2EmailPhoneAsync ||
    type === NotificationType.LeadRevealV2PhoneAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailAsync ||
    type === NotificationType.LeadRevealV2PersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailPhoneAsync ||
    type === NotificationType.LeadRevealV2EmailPersonalEmailAsync
  ) {
    return 'View Leads';
  }
  if (
    type === NotificationType.CompanyEnrichV2Async ||
    type === NotificationType.CompanyRevealV2Async ||
    type === NotificationType.CompanyEnrichFreeV2Async
  ) {
    return 'View Companies';
  }
  if (
    type === NotificationType.EmailVerifier ||
    type === NotificationType.AiColumns
  ) {
    return 'View';
  }
  if (type === NotificationType.AiColumnsFailed) {
    return 'View Failed';
  }
  return '';
};

export const getProgressBarPercentage = (
  completedCount: number,
  totalCount: number,
  maxPercentageValue = 99,
): number => {
  if (totalCount === 0) return 0;
  const percentage = Math.round((completedCount / totalCount) * 100);
  return Math.min(percentage, maxPercentageValue);
};
